import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { DeliverableBadge, DeliverableStatusBadge } from "@/components/Badges";
import { EditorBubble } from "@/components/EditorBubble";
import { ClientAvatar } from "@/components/ClientAvatar";
import { DELIVERABLE_META, relativeDue, formatDate } from "@/lib/constants";
import {
  CalendarDays, List, ChevronLeft, ChevronRight, Loader2, MapPin, AlertTriangle, Square, Columns3, History,
} from "lucide-react";
import {
  format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isToday, isBefore, startOfDay,
  addMonths, subMonths, addWeeks, subWeeks, addDays, subDays,
} from "date-fns";
import { toast } from "sonner";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const VIEWS = [
  { key: "day", label: "Day", icon: Square },
  { key: "week", label: "Week", icon: Columns3 },
  { key: "month", label: "Month", icon: CalendarDays },
  { key: "agenda", label: "Agenda", icon: List },
];

function isOverdue(it) {
  return it.status !== "DELIVERED" && isBefore(parseISO(it.due_date), startOfDay(new Date()));
}

function AgendaCard({ it, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex w-full items-center justify-between gap-3 rounded-xl border bg-card px-4 py-3 text-left transition-transform hover:-translate-y-0.5 hover:border-primary/40 ${it.status === "DELIVERED" ? "opacity-60" : ""} ${isOverdue(it) ? "border-rose-500/40" : "border-border"}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <DeliverableBadge type={it.type} />
          <span className={`text-sm font-medium ${it.status === "DELIVERED" ? "line-through decoration-2" : ""}`}>{it.title}</span>
          {it.is_reshoot && (
            <span title="Reshoot — property has multiple shoots"
              className="inline-flex items-center gap-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-300">
              <History className="h-2.5 w-2.5" /> Reshoot
            </span>
          )}
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{it.address}</span>
          {it.client_name && (<><span className="text-muted-foreground/40">·</span><ClientAvatar name={it.client_name} className="h-4 w-4 text-[7px]" /><span className="truncate">{it.client_name}</span></>)}
          {it.editor_name && (
            <EditorBubble name={it.editor_name} color={it.editor_color} link={it.editor_link}
              className="ml-1 px-1.5 py-0.5 text-[10px]" showIcon={false} />
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        {it.due_date && it.status !== "DELIVERED" && (
          <span title={formatDate(it.due_date)}
            className={`text-xs font-semibold ${isOverdue(it) ? "text-rose-500 dark:text-rose-400" : "text-foreground"}`}>
            {relativeDue(it.due_date)}
          </span>
        )}
        <DeliverableStatusBadge status={it.status} />
      </div>
    </button>
  );
}

export default function CalendarPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => {
    const saved = localStorage.getItem("calendarView");
    return VIEWS.some((v) => v.key === saved) ? saved : "month";
  });
  const [cursor, setCursor] = useState(new Date());
  const navigate = useNavigate();

  useEffect(() => { localStorage.setItem("calendarView", view); }, [view]);

  useEffect(() => {
    api.get("/calendar/deliverables")
      .then(({ data }) => setItems(data))
      .catch((err) => toast.error(apiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const byDate = useMemo(() => {
    const map = {};
    for (const it of items) (map[it.due_date] = map[it.due_date] || []).push(it);
    return map;
  }, [items]);

  const monthDays = useMemo(
    () => eachDayOfInterval({ start: startOfWeek(startOfMonth(cursor)), end: endOfWeek(endOfMonth(cursor)) }),
    [cursor]
  );
  const weekDays = useMemo(
    () => eachDayOfInterval({ start: startOfWeek(cursor), end: endOfWeek(cursor) }),
    [cursor]
  );

  const agendaGroups = useMemo(() => {
    const groups = {};
    for (const it of items) (groups[it.due_date] = groups[it.due_date] || []).push(it);
    return Object.keys(groups).sort().map((d) => ({ date: d, items: groups[d] }));
  }, [items]);

  // The agenda date to scroll to: today if present, else the first upcoming date.
  const agendaScrollDate = useMemo(() => {
    const today = format(new Date(), "yyyy-MM-dd");
    if (agendaGroups.some((g) => g.date === today)) return today;
    return agendaGroups.find((g) => g.date >= today)?.date || null;
  }, [agendaGroups]);

  const agendaScrollRef = useRef(null);
  useEffect(() => {
    if (view === "agenda" && !loading && agendaScrollRef.current) {
      agendaScrollRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [view, loading, agendaScrollDate]);

  const goDetail = (it) => navigate(`/orders/${it.order_id}`);

  const step = (dir) => {
    if (view === "day") setCursor((c) => (dir > 0 ? addDays(c, 1) : subDays(c, 1)));
    else if (view === "week") setCursor((c) => (dir > 0 ? addWeeks(c, 1) : subWeeks(c, 1)));
    else setCursor((c) => (dir > 0 ? addMonths(c, 1) : subMonths(c, 1)));
  };

  const navTitle = () => {
    if (view === "day") return format(cursor, "EEEE, MMMM d, yyyy");
    if (view === "week") return `${format(startOfWeek(cursor), "MMM d")} – ${format(endOfWeek(cursor), "MMM d, yyyy")}`;
    return format(cursor, "MMMM yyyy");
  };

  const dayItems = (byDate[format(cursor, "yyyy-MM-dd")] || []);

  return (
    <div>
      <PageHeader title="Calendar" subtitle={`${items.length} scheduled deliverables`} testid="calendar-header">
        <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1">
          {VIEWS.map((v) => (
            <button key={v.key} onClick={() => setView(v.key)} data-testid={`view-${v.key}-btn`}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${view === v.key ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground"}`}>
              <v.icon className="h-4 w-4" /> {v.label}
            </button>
          ))}
        </div>
      </PageHeader>

      <div className="p-6 md:p-8">
        {loading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border py-16 text-center" data-testid="calendar-empty">
            <CalendarDays className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No due dates set yet. Open an order and set a due date on any deliverable.</p>
          </div>
        ) : (
          <>
            {view !== "agenda" && (
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-heading text-xl font-bold tracking-tight" data-testid="calendar-nav-title">{navTitle()}</h2>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="calendar-today-btn">Today</Button>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => step(-1)} data-testid="calendar-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => step(1)} data-testid="calendar-next-btn"><ChevronRight className="h-4 w-4" /></Button>
                </div>
              </div>
            )}

            {/* DAY VIEW */}
            {view === "day" && (
              <div className="space-y-2" data-testid="calendar-day">
                {dayItems.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border py-16 text-center text-sm text-muted-foreground">
                    Nothing due on this day.
                  </div>
                ) : (
                  dayItems.map((it) => <AgendaCard key={it.deliverable_id} it={it} onClick={() => goDetail(it)} testid={`day-item-${it.deliverable_id}`} />)
                )}
              </div>
            )}

            {/* WEEK VIEW */}
            {view === "week" && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7" data-testid="calendar-week">
                {weekDays.map((day) => {
                  const key = format(day, "yyyy-MM-dd");
                  const list = byDate[key] || [];
                  return (
                    <div key={key} data-testid={`week-col-${key}`}
                      className={`flex flex-col rounded-xl border bg-card/30 ${isToday(day) ? "border-primary/50" : "border-border"}`}>
                      <div className="flex items-center justify-between border-b border-border px-3 py-2">
                        <div>
                          <div className="overline text-muted-foreground">{format(day, "EEE")}</div>
                          <div className={`font-heading text-lg font-bold ${isToday(day) ? "text-primary" : ""}`}>{format(day, "d")}</div>
                        </div>
                        {list.length > 0 && <span className="font-mono text-xs text-muted-foreground">{list.length}</span>}
                      </div>
                      <div className="flex-1 space-y-1.5 p-2" style={{ minHeight: 120 }}>
                        {list.map((it) => (
                          <button key={it.deliverable_id} onClick={() => goDetail(it)} data-testid={`week-item-${it.deliverable_id}`}
                            className={`w-full rounded-md border px-2 py-1.5 text-left transition-transform hover:-translate-y-0.5 ${it.status === "DELIVERED" ? "opacity-50" : ""} ${isOverdue(it) ? "border-rose-500/40" : "border-border"} ${DELIVERABLE_META[it.type] || DELIVERABLE_META.Other}`}
                            title={`${it.type} · ${it.address}`}>
                            <div className={`flex items-center gap-1 truncate text-[11px] font-semibold ${it.status === "DELIVERED" ? "line-through decoration-2" : ""}`}>
                              {it.is_reshoot && <History className="h-2.5 w-2.5 shrink-0 text-amber-500" title="Reshoot" />}
                              <span className="truncate">{it.type}</span>
                            </div>
                            <div className="truncate text-[10px] opacity-80">{it.address}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* MONTH VIEW */}
            {view === "month" && (
              <div className="overflow-hidden rounded-xl border border-border bg-card" data-testid="calendar-month">
                <div className="grid grid-cols-7 border-b border-border">
                  {WEEKDAYS.map((w) => (
                    <div key={w} className="overline px-3 py-2.5 text-center text-muted-foreground">{w}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {monthDays.map((day) => {
                    const key = format(day, "yyyy-MM-dd");
                    const list = byDate[key] || [];
                    const inMonth = isSameMonth(day, cursor);
                    return (
                      <div key={key} className={`min-h-[112px] border-b border-r border-border p-1.5 ${inMonth ? "" : "bg-secondary/30"}`}>
                        <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs ${isToday(day) ? "bg-primary font-semibold text-primary-foreground" : inMonth ? "text-foreground" : "text-muted-foreground"}`}>
                          {format(day, "d")}
                        </div>
                        <div className="space-y-1">
                          {list.slice(0, 3).map((it) => (
                            <button key={it.deliverable_id} onClick={() => goDetail(it)} data-testid={`calendar-item-${it.deliverable_id}`}
                              className={`w-full truncate rounded border px-1.5 py-0.5 text-left text-[10px] font-medium transition-transform hover:-translate-y-0.5 ${it.status === "DELIVERED" ? "opacity-50 line-through " : ""}${DELIVERABLE_META[it.type] || DELIVERABLE_META.Other} ${isOverdue(it) ? "ring-1 ring-rose-500/50" : ""}`}
                              title={`${it.type} · ${it.address}${it.is_reshoot ? " · Reshoot" : ""}`}>
                              {it.is_reshoot && <History className="mr-0.5 inline h-2.5 w-2.5 text-amber-500" />}
                              {it.type}
                            </button>
                          ))}
                          {list.length > 3 && <div className="px-1 text-[10px] text-muted-foreground">+{list.length - 3} more</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* AGENDA VIEW */}
            {view === "agenda" && (
              <div className="space-y-6" data-testid="calendar-agenda">
                {agendaGroups.map((g) => {
                  const d = parseISO(g.date);
                  const overdueDay = isBefore(d, startOfDay(new Date())) && !isToday(d);
                  return (
                    <div key={g.date} ref={g.date === agendaScrollDate ? agendaScrollRef : null} className="scroll-mt-24">
                      <div className="mb-3 flex items-center gap-2">
                        <span className={`font-heading text-sm font-bold ${isToday(d) ? "text-primary" : ""}`}>{format(d, "EEEE, MMM d")}</span>
                        {isToday(d) && <span className="overline text-primary">Today</span>}
                        {overdueDay && <span className="flex items-center gap-1 text-[11px] font-medium text-rose-400"><AlertTriangle className="h-3 w-3" /> Overdue</span>}
                      </div>
                      <div className="space-y-2">
                        {g.items.map((it) => <AgendaCard key={it.deliverable_id} it={it} onClick={() => goDetail(it)} testid={`agenda-item-${it.deliverable_id}`} />)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
